import { useState, useCallback, useRef } from 'react';

import { OnChangeFn, RowSelectionState } from '@tanstack/react-table';
import Papa from 'papaparse';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useMatchDocumentsByFilename } from '@/api/mutations/useMatchDocumentsByFilename';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Switch } from '@/new/components/shadcn/switch';
import { Tabs, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';
import { cn } from '@/new/lib/utils';

type MatchMode = 'filename' | 'controlNumber';
type SelectFromListSource = 'subset_picker' | 'docs_page';

interface SelectFromListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: string;
  source: SelectFromListSource;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  onApplyMatchedDocIds?: (docIds: string[]) => Promise<void> | void;
  inputDescription: string;
}

type DialogStep = 'input' | 'results';

interface MatchResult {
  matchedDocIds: string[];
  unmatchedFilenames: string[];
  duplicateMatches: { filename: string; count: number }[];
  totalProvided: number;
  matchedCount: number;
}

interface ParseCSVResult {
  uniqueFilenames: string[];
  duplicateFilenames: string[];
}

function buildTemplateCsv(matchMode: MatchMode) {
  if (matchMode === 'controlNumber') {
    return {
      filename: 'relativity-identifier-list-template.csv',
      content: ['relativity identifier', 'WX0001473', 'WX0001474', 'WX0001475'].join('\r\n'),
    };
  }

  return {
    filename: 'document-list-template.csv',
    content: ['filename', 'Contract_v2.pdf', 'meeting_notes.docx', 'invoice_2024.pdf'].join('\r\n'),
  };
}

function parseCSV(content: string, matchMode: MatchMode): ParseCSVResult {
  const result = Papa.parse<string[]>(content, {
    skipEmptyLines: true,
  });

  // Filter out non-critical errors (like UndetectableDelimiter which just means it defaulted to comma)
  const criticalErrors = result.errors.filter((e) => e.type !== 'Delimiter');
  if (criticalErrors.length > 0) {
    throw new Error('Failed to parse CSV');
  }

  if (result.data.length === 0) return { uniqueFilenames: [], duplicateFilenames: [] };

  // Check if the first line is a header (case-insensitive check for "filename" or the
  // Relativity Identifier — "control number" is still accepted as a legacy header alias).
  const firstCell = result.data[0]?.[0]?.trim().toLowerCase() ?? '';
  const filenameHeaders = ['filename', 'file name', 'file_name'];
  const identifierHeaders = [
    'relativity identifier',
    'relativity_identifier',
    'relativityidentifier',
    'control number',
    'control_number',
    'controlnumber',
  ];
  const validHeaders = matchMode === 'controlNumber' ? identifierHeaders : filenameHeaders;
  const hasHeader = validHeaders.includes(firstCell);

  const dataRows = hasHeader ? result.data.slice(1) : result.data;
  const allFilenames = dataRows.map((row) => row[0]?.trim()).filter((value): value is string => !!value);

  // Deduplicate and track duplicates
  const seen = new Set<string>();
  const uniqueFilenames: string[] = [];
  const duplicateFilenames: string[] = [];

  for (const filename of allFilenames) {
    if (seen.has(filename)) {
      duplicateFilenames.push(filename);
    } else {
      seen.add(filename);
      uniqueFilenames.push(filename);
    }
  }

  return { uniqueFilenames, duplicateFilenames };
}

export function SelectFromListDialog({
  open,
  onOpenChange,
  matterId,
  source,
  onRowSelectionChange,
  onApplyMatchedDocIds,
  inputDescription,
}: SelectFromListDialogProps) {
  const [step, setStep] = useState<DialogStep>('input');
  const [duplicateFilenames, setDuplicateFilenames] = useState<string[]>([]);
  const [matchResult, setMatchResult] = useState<MatchResult | null>(null);
  const [matchMode, setMatchMode] = useState<MatchMode>('filename');
  const [uploadedFilenames, setUploadedFilenames] = useState<string[]>([]);
  const [ignoreFileExtensions, setIgnoreFileExtensions] = useState(false);
  const matchRequestIdRef = useRef(0);
  const ignoreFileExtensionsRef = useRef(false);

  const { mutateAsync: matchDocuments, isLoading } = useMatchDocumentsByFilename();
  const { data: matter } = useGetMatterV2(matterId);

  const hasRelativityEnabled = !!matter?.relativityWorkspaceId;

  const handleDownloadTemplate = async () => {
    try {
      const { filename, content } = buildTemplateCsv(matchMode);
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Downloading template');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download template');
    }
  };

  const resetState = useCallback(() => {
    matchRequestIdRef.current += 1;
    setStep('input');
    setDuplicateFilenames([]);
    setMatchResult(null);
    setUploadedFilenames([]);
  }, []);

  const runMatch = useCallback(
    async (filenames: string[], shouldIgnoreFileExtensions: boolean) => {
      return matchDocuments({
        matterId,
        filenames,
        matchBy: matchMode,
        ignoreFileExtensions: matchMode === 'filename' && shouldIgnoreFileExtensions,
      });
    },
    [matchDocuments, matchMode, matterId],
  );

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        resetState();
      }
      onOpenChange(newOpen);
    },
    [onOpenChange, resetState],
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const requestId = ++matchRequestIdRef.current;
      const file = acceptedFiles[0];
      const reader = new FileReader();

      reader.onerror = () => {
        toast.error('Failed to read file. Please try again or use a different file.');
      };

      reader.onabort = () => {
        toast.error('File reading was interrupted. Please try again.');
      };

      reader.onload = async (e) => {
        if (requestId !== matchRequestIdRef.current) return;

        const content = e.target?.result as string;

        let uniqueFilenames: string[];
        let dupes: string[];

        try {
          const parsed = parseCSV(content, matchMode);
          uniqueFilenames = parsed.uniqueFilenames;
          dupes = parsed.duplicateFilenames;
        } catch {
          toast.error('Something went wrong trying to parse your CSV. Try again with a different file.');
          return;
        }

        setDuplicateFilenames(dupes);
        setUploadedFilenames(uniqueFilenames);

        if (uniqueFilenames.length === 0) {
          toast.error(
            matchMode === 'controlNumber'
              ? 'No valid Relativity Identifiers found in CSV'
              : 'No valid filenames found in CSV',
          );
          return;
        }

        let result: MatchResult;
        try {
          result = await runMatch(uniqueFilenames, ignoreFileExtensionsRef.current);
        } catch {
          return;
        }

        if (requestId !== matchRequestIdRef.current) return;

        trackCustomEvent(TrackingEventsV2.DocumentSelectFromListFileSubmitted, {
          source,
          matchMode,
          totalProvided: result.totalProvided,
          matchedCount: result.matchedCount,
        });

        setMatchResult(result);
        setStep('results');
      };

      reader.readAsText(file);
    },
    [matchMode, runMatch, source],
  );

  const handleIgnoreFileExtensionsChange = async (checked: boolean) => {
    ignoreFileExtensionsRef.current = checked;
    setIgnoreFileExtensions(checked);

    if (step !== 'results' || uploadedFilenames.length === 0) return;

    const requestId = ++matchRequestIdRef.current;
    try {
      const result = await runMatch(uploadedFilenames, checked);
      if (requestId !== matchRequestIdRef.current) return;
      setMatchResult(result);
    } catch {
      if (requestId !== matchRequestIdRef.current) return;
      ignoreFileExtensionsRef.current = !checked;
      setIgnoreFileExtensions(!checked);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected: () => {
      toast.error('Invalid file type. Please upload a CSV file.');
    },
    accept: {
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
    multiple: false,
  });

  const handleApplySelection = async () => {
    if (!matchResult) return;

    try {
      if (onApplyMatchedDocIds) {
        await onApplyMatchedDocIds(matchResult.matchedDocIds);
      } else if (onRowSelectionChange) {
        // Add matched doc IDs to selection (additive)
        onRowSelectionChange((prev) => {
          const updated = { ...prev };
          matchResult.matchedDocIds.forEach((id) => {
            updated[id] = true;
          });
          return updated;
        });
      }

      handleOpenChange(false);
    } catch (error) {
      console.error('Failed to apply document selection:', error);
      toast.error('Failed to apply matched documents');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl" showCloseButton>
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--list text-teal-500" /> Select Documents From a List
          </DialogTitle>
          <DialogDescription>
            {step === 'input' ? inputDescription : 'Review the matching documents in this Matter.'}
          </DialogDescription>
        </DialogHeader>

        {matchMode === 'filename' && (
          <label className="mb-5 flex w-fit cursor-pointer items-center gap-5 text-sm text-gray-700">
            <Switch
              checked={ignoreFileExtensions}
              disabled={isLoading}
              onCheckedChange={handleIgnoreFileExtensionsChange}
            />
            <div className="space-y-0.5">
              <p className="font-medium">Ignore Wexler file extensions</p>
              <p className="text-xs text-gray-500">Select this option if your CSV doesn't include file extensions.</p>
            </div>
          </label>
        )}

        {step === 'input' && (
          <div className="relative">
            <div
              className={cn(
                'flex flex-col gap-4 transition-all duration-200',
                isLoading && 'pointer-events-none opacity-60 blur-sm',
              )}
            >
              {hasRelativityEnabled && (
                <div>
                  <Tabs value={matchMode} onValueChange={(value) => setMatchMode(value as MatchMode)}>
                    <TabsList className="h-auto w-full rounded-lg border-none bg-gray-100 p-1">
                      <TabsTrigger
                        value="filename"
                        className="grow basis-1/2 rounded-md border-none py-1.5 font-normal text-gray-400 data-[state=active]:mb-0 data-[state=active]:bg-white data-[state=active]:text-gray-800"
                      >
                        List of Filenames
                      </TabsTrigger>
                      <TabsTrigger
                        value="controlNumber"
                        className="grow basis-1/2 rounded-md border-none py-1.5 font-normal text-gray-400 data-[state=active]:mb-0 data-[state=active]:bg-white data-[state=active]:text-purple-500"
                      >
                        <Icon className="lucide--database mr-2" /> List of Relativity Identifiers
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              )}

              <p className="text-sm text-gray-600">
                {matchMode === 'controlNumber'
                  ? "The file should contain a single column with a header of 'relativity identifier' (the legacy 'control number' header is also accepted). Identifiers are case-sensitive, and we automatically trim any leading or trailing spaces."
                  : "The file should contain a single column with a header of 'filename'. Filenames are case-sensitive, and we automatically trim any leading or trailing spaces. To see an example, download the CSV template below."}
              </p>

              <Button onClick={handleDownloadTemplate}>
                <Icon className="lucide--download" /> Download CSV Template
              </Button>

              <div
                {...getRootProps()}
                className={cn(
                  'group flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-6 transition-colors',
                  isDragActive
                    ? matchMode === 'controlNumber'
                      ? 'border-purple-400 bg-purple-50/60'
                      : 'border-blue-400 bg-blue-50/60'
                    : 'border-gray-300 hover:border-gray-400',
                )}
              >
                <input {...getInputProps()} />

                <div
                  className={cn(
                    'relative h-24 w-full max-w-48 overflow-hidden rounded-md border duration-150',
                    matchMode === 'controlNumber' ? 'bg-purple-50' : 'bg-gray-50',
                    matchMode === 'controlNumber'
                      ? isDragActive
                        ? 'border-purple-400'
                        : 'border-purple-300 group-hover:border-purple-400'
                      : isDragActive
                        ? 'border-blue-300'
                        : 'group-hover:border-gray-300',
                  )}
                >
                  <div
                    className={cn(
                      'absolute top-3.5 left-3.5 flex h-full w-full rounded-md border bg-white text-[11px]',
                      matchMode === 'controlNumber' && 'border-purple-300',
                    )}
                  >
                    <div className="border-r pt-0.5">
                      {matchMode === 'controlNumber' ? (
                        <>
                          <p className="flex h-5.5 items-center border-b px-2 pr-3 text-purple-400">
                            relativity identifier
                          </p>
                          <p className="flex h-5.5 items-center border-b px-2 pr-3 text-purple-600">WX0001473</p>
                          <p className="flex h-5.5 items-center border-b px-2 pr-3 text-purple-600">WX0001474</p>
                          <p className="flex h-5.5 items-center border-b px-2 pr-3 text-purple-600">WX0001475</p>
                        </>
                      ) : (
                        <>
                          <p className="flex h-5.5 items-center border-b px-2 pr-3 text-gray-400">filename</p>
                          <p className="flex h-5.5 items-center border-b px-2 pr-3">Contract_v2.pdf</p>
                          <p className="flex h-5.5 items-center border-b px-2 pr-3">meeting_notes.docx</p>
                          <p className="flex h-5.5 items-center border-b px-2 pr-3">invoice_2024.pdf</p>
                        </>
                      )}
                    </div>
                    <div className="grow pt-0.5">
                      <p className="h-5.5 border-b"></p>
                      <p className="h-5.5 border-b"></p>
                      <p className="h-5.5 border-b"></p>
                      <p className="h-5.5 border-b"></p>
                    </div>
                  </div>
                </div>

                <p className="mt-2 text-center text-xs text-gray-600">
                  Drag and drop a CSV here or <span className="text-blue-500">click to browse files</span>
                </p>
              </div>
            </div>

            <div
              className={`${isLoading ? 'opacity-100' : 'opacity-0'} pointer-events-none absolute inset-0 flex items-center justify-center duration-150`}
            >
              <div className="-mt-3 flex items-center gap-2 rounded-lg border bg-gray-50 px-3 py-2 shadow-lg/3">
                <Icon className="lucide--loader-circle animate-spin text-blue-500" />
                <span className="text-gray-600">Processing CSV...</span>
              </div>
            </div>
          </div>
        )}

        {step === 'results' && matchResult && (
          <>
            <div className={cn('space-y-3', isLoading && 'pointer-events-none opacity-60')}>
              {matchResult.matchedCount > 0 ? (
                <div className="flex items-center gap-2 rounded-md border border-green-300 bg-green-50 px-3 py-2 text-green-600">
                  <Icon className="lucide--check" />
                  <p>
                    Matched {matchResult.matchedCount} document{matchResult.matchedCount === 1 ? '' : 's'} in this
                    Matter.
                    {matchResult.duplicateMatches.length > 0 && (
                      <>
                        {' '}
                        <Popover>
                          <PopoverTrigger className="underline decoration-green-400 decoration-dashed underline-offset-4">
                            {matchResult.duplicateMatches.length}{' '}
                            {matchMode === 'controlNumber' ? 'Relativity Identifier' : 'filename'}
                            {matchResult.duplicateMatches.length === 1 ? ' was' : 's were'}
                          </PopoverTrigger>
                          <PopoverContent className="max-w-lg text-gray-700">
                            <ol className="space-y-0.5">
                              {matchResult.duplicateMatches.map((d, i) => (
                                <li key={i}>
                                  <span className="text-gray-400 tabular-nums">{i + 1}.</span> {d.filename}{' '}
                                  <span className="text-gray-400">({d.count} matches)</span>
                                </li>
                              ))}
                            </ol>
                          </PopoverContent>
                        </Popover>{' '}
                        matched by multiple documents.
                      </>
                    )}
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-600">
                  <Icon className="lucide--alert-circle" />
                  <p>
                    The list uploaded didn't match any{' '}
                    {matchMode === 'controlNumber' ? 'Relativity Identifiers' : 'filenames'} in this Matter
                  </p>
                </div>
              )}

              {duplicateFilenames.length > 0 && (
                <div className="flex items-center gap-2 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-gray-600">
                  <Icon className="lucide--info" />
                  <p>
                    Ignored{' '}
                    <Popover>
                      <PopoverTrigger className="underline decoration-gray-300 decoration-dashed underline-offset-4">
                        {duplicateFilenames.length} duplicate{' '}
                        {matchMode === 'controlNumber' ? 'Relativity Identifier' : 'filename'}
                        {duplicateFilenames.length === 1 ? '' : 's'}
                      </PopoverTrigger>
                      <PopoverContent>
                        <ol className="space-y-0.5">
                          {duplicateFilenames.map((d, i) => (
                            <li key={i}>
                              <span className="text-gray-400 tabular-nums">{i + 1}.</span> {d}
                            </li>
                          ))}
                        </ol>
                      </PopoverContent>
                    </Popover>{' '}
                    found in uploaded list
                  </p>
                </div>
              )}

              {matchResult.unmatchedFilenames.length > 0 && (
                <div className="mt-6">
                  <div className="mb-3 ml-3.25">
                    <p className="flex items-center gap-2 font-medium text-gray-600">
                      <Icon className="lucide--alert-circle text-red-500" /> {matchResult.unmatchedFilenames.length}{' '}
                      {matchMode === 'controlNumber'
                        ? matchResult.unmatchedFilenames.length === 1
                          ? 'Relativity Identifier'
                          : 'Relativity Identifiers'
                        : matchResult.unmatchedFilenames.length === 1
                          ? 'filename'
                          : 'filenames'}{' '}
                      not found in Matter
                    </p>

                    <p className="mt-1 text-xs">
                      We couldn't find any files that matched{' '}
                      {matchResult.unmatchedFilenames.length === 1 ? 'this' : 'these'}{' '}
                      {matchMode === 'controlNumber'
                        ? matchResult.unmatchedFilenames.length === 1
                          ? 'Relativity Identifier'
                          : 'Relativity Identifiers'
                        : matchResult.unmatchedFilenames.length === 1
                          ? 'filename'
                          : 'filenames'}
                      .
                    </p>
                  </div>

                  <div className="mx-0.5 max-h-54 overflow-y-auto rounded-md border border-gray-300 py-px">
                    {matchResult.unmatchedFilenames.map((filename, i) => (
                      <div className="flex items-center gap-2 px-3 py-1.5 pt-2 pb-2 not-last:border-b" key={i}>
                        <span className="text-gray-400 tabular-nums">{i + 1}.</span> {filename}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button onClick={resetState} disabled={isLoading}>
                <Icon className="lucide--arrow-left" />
                Back
              </Button>
              <Button
                onClick={handleApplySelection}
                disabled={isLoading || matchResult?.matchedCount === 0}
                variant={'primary'}
              >
                <Icon className="lucide--check" />
                Select {matchResult?.matchedCount} Documents
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
